class WebSocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectTimer = null;
  }

  connect() {
    // Utiliser une URL relative qui s'adapte automatiquement au protocole (HTTP/HTTPS)
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws/dashboard`;
    
    console.log('🔌 Connexion WebSocket vers:', wsUrl);

    try {
      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = () => {
        console.log('✅ WebSocket connecté');
        this.reconnectAttempts = 0;
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
        this.emit('connection', { status: 'connected' });
      };

      this.socket.onclose = (event) => {
        console.log('❌ WebSocket déconnecté:', event.code, event.reason);
        this.emit('connection', { status: 'disconnected', reason: event.reason });
        this.scheduleReconnect();
      };

      this.socket.onerror = (error) => {
        console.error('🚫 Erreur WebSocket:', error);
        this.emit('connection', { 
          status: 'error', 
          error: error.message || 'Connection error',
          attempts: this.reconnectAttempts 
        });
      };

      this.socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📨 Message WebSocket reçu:', data);
          
          // Dispatcher selon le type de message
          switch (data.type) {
            case 'station_update':
              console.log('📡 Mise à jour station:', data);
              this.emit('station_update', data);
              break;
            case 'command_update':
              console.log('⚡ Mise à jour commande:', data);
              this.emit('command_update', data);
              break;
            case 'heartbeat':
              // Heartbeat silencieux
              this.emit('heartbeat', data);
              break;
            default:
              console.log('🔔 Événement système:', data);
              this.emit('system_event', data);
          }
        } catch (error) {
          console.error('❌ Erreur parsing message WebSocket:', error);
        }
      };

    } catch (error) {
      console.error('❌ Erreur création WebSocket:', error);
      this.scheduleReconnect();
    }

    return this;
  }

  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('❌ Nombre maximum de tentatives de reconnexion atteint');
      return;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    console.log(`🔄 Tentative de reconnexion dans ${delay}ms (tentative ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);
    
    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    if (this.socket) {
      console.log('🔌 Déconnexion WebSocket');
      this.socket.close();
      this.socket = null;
    }
  }

  // S'abonner à un canal spécifique (ex: station spécifique)
  subscribe(channel, data = {}) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log(`📢 Abonnement au canal: ${channel}`, data);
      this.socket.send(JSON.stringify({ 
        type: 'subscribe', 
        channel, 
        ...data 
      }));
    }
  }

  // Se désabonner d'un canal
  unsubscribe(channel, data = {}) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log(`📢 Désabonnement du canal: ${channel}`, data);
      this.socket.send(JSON.stringify({ 
        type: 'unsubscribe', 
        channel, 
        ...data 
      }));
    }
  }

  // Envoyer un message via WebSocket
  send(event, data) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ 
        type: event, 
        ...data 
      }));
    } else {
      console.warn('⚠️ WebSocket non connecté, impossible d\'envoyer:', event, data);
    }
  }

  // Ajouter un listener pour un événement
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
  }

  // Supprimer un listener
  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  // Émettre un événement vers les listeners
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Erreur dans le listener ${event}:`, error);
        }
      });
    }
  }

  // Vérifier si connecté
  isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }

  // Obtenir le statut de connexion
  getConnectionStatus() {
    if (!this.socket) return 'disconnected';
    switch (this.socket.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
        return 'disconnecting';
      case WebSocket.CLOSED:
        return 'disconnected';
      default:
        return 'unknown';
    }
  }
}

// Instance singleton
export const wsService = new WebSocketService();

// Hook React pour utiliser WebSocket
export const useWebSocket = () => {
  return wsService;
};

export default wsService;
